import { cn } from "@/shared/lib/utils";

export function CircularProgress({ percent, size = 52, strokeWidth = 5 }: { percent: number; size?: number; strokeWidth?: number }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-emerald-500/15" />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="url(#progressGradient)" strokeWidth={strokeWidth}
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#34d399" />
            <stop offset="100%" stopColor="#22c55e" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold text-emerald-300">{percent}%</span>
      </div>
    </div>
  );
}

export function ProgressBar({ percent }: { percent: number }) {
  const barColor = percent >= 80 ? "bg-green-500" : percent >= 50 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-3 w-full">
      <div className="flex-1 h-2 rounded-full bg-border/20 overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-700 ease-out", barColor)} style={{ width: `${percent}%` }} />
      </div>
      <span className="text-sm font-medium text-muted-foreground w-10 text-right tabular-nums">{percent}%</span>
    </div>
  );
}

export function CompactScoreBar({ score, widthClassName = "w-[156px]" }: { score: number | null; widthClassName?: string }) {
  if (score == null) {
    return <span className={cn(widthClassName, "text-right text-xs text-muted-foreground/60")}>—</span>;
  }

  const color = score >= 80 ? "bg-emerald-500" : score >= 50 ? "bg-amber-500" : "bg-red-500";
  const textColor = score >= 80 ? "text-emerald-400" : score >= 50 ? "text-amber-400" : "text-red-400";

  return (
    <div className={cn("flex items-center gap-2", widthClassName)}>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted/60">
        <div className={cn("h-full rounded-full", color)} style={{ width: `${Math.min(score, 100)}%` }} />
      </div>
      <span className={cn("w-10 text-right text-xs font-semibold tabular-nums", textColor)}>
        {Math.round(score)}%
      </span>
    </div>
  );
}
